import React, { useState, useCallback } from 'react';
import './TransactionsDetails.scss';
import { Loader } from '../../../UiKit';
import { EmptyState } from '../../commons';
import { InvalidSearchImg } from '../../../images/images';
import useRetryRefund from '../../../hooks/useRetryRefund';
import { useQueryCall } from '../../../api/react-client';
import { lifelineEndPoints } from '../../../api/end-points';

const ValueComp = ({ valueDetails, metaData, refetchTransactionDetails }) => {
  const { retryRefund } = useRetryRefund(refetchTransactionDetails);
  const handleValueClick = useCallback(() => {
    let endpoint = valueDetails?.on_click;
    if (!endpoint) return;
    endpoint = endpoint.substring(1, endpoint.length);
    const mutateParams = {
      slug: endpoint,
      data: {
        refund_id: metaData?.refund_id,
        ticket_id: metaData?.ticket_id,
      },
    };
    retryRefund(mutateParams);
  }, [metaData, valueDetails, retryRefund]);

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <span
      className={`tw-text-200 tw-rounded-xl tw-mr-2 ${
        valueDetails?.text_size === 'regular' ? 'tw-text-200' : 'tw-text-[9px]'
      } tw-flex tw-items-center ${
        valueDetails?.on_click ? 'tw-cursor-pointer' : ''
      } ${valueDetails?.bg_color ? 'tw-px-2' : ''}`}
      css={`
        background-color: ${valueDetails?.bg_color};
        color: ${valueDetails?.text_color};
        font-weight: ${valueDetails?.bg_color ? '600' : '500'};
      `}
      onClick={handleValueClick}
      data-dd-privacy='mask'
    >
      {valueDetails?.image && (
        <div className='tw-h-10' data-dd-privacy='hidden'>
          <img
            src={valueDetails?.image}
            alt=''
            className='tw-h-full tw-w-full'
          />
        </div>
      )}
      {valueDetails?.text}
      {!valueDetails?.image && !valueDetails?.text && '_'}
    </span>
  );
};

const PaymentCards = ({ paymentDetails }) => {
  return (
    <div className='tw-w-full tw-flex tw-font-medium tw-text-grey-600'>
      <div className='tw-flex-1 tw-pr-8 tw-p-5'>
        {paymentDetails.fields.map((payment, index) => {
          if (index % 2 === 0)
            return (
              <div className='tw-flex tw-mb-2 tw-justify-between'>
                <span className='tw-text-200'>{payment.label}</span>
                <span className='tw-flex tw-flex-1 tw-justify-end'>
                  {payment?.value.map((valueDetails, index) => {
                    return (
                      <ValueComp
                        key={`payment_value_even_${index}`}
                        valueDetails={valueDetails}
                      />
                    );
                  })}
                </span>
              </div>
            );
        })}
      </div>
      <div className='tw-w-1 tw-border-solid tw-border-[1px] tw-border-l-0 tw-border-t-0 tw-border-b-0 tw-border-grey-200 tw-rounded-xl'></div>
      <div className='tw-flex-1 tw-pl-8 tw-p-5'>
        {paymentDetails.fields.map((payment, index) => {
          if (index % 2 != 0)
            return (
              <div className='tw-flex tw-mb-2 tw-justify-between'>
                <span className='tw-text-200 tw-my-auto'>{payment.label}</span>
                <span className='tw-flex tw-gap-1 tw-justify-end'>
                  {payment?.value.map((valueDetails, index) => {
                    return (
                      <ValueComp
                        key={`payment_value_odd_${index}`}
                        valueDetails={valueDetails}
                      />
                    );
                  })}
                </span>
              </div>
            );
        })}
      </div>
    </div>
  );
};

const RefundDetailsComp = React.memo(function refundDetailsComponent({
  refunds,
  refetchTransactionDetails,
}) {
  return (
    <div
      className={`tw-flex-col  ${
        refunds?.length > 0 ? 'tw-pl-5 tw-pr-5 tw-pt-5' : 'tw-p-5'
      }`}
      data-dd-privacy='mask'
    >
      <div>
        {refunds.length === 0 ? (
          <div className='tw-bg-grey-100 tw-py-4 tw-flex tw-items-center tw-justify-center tw-rounded-xl tw-mb-4'>
            <div className='tw-text-grey-600'>
              There are no Refunds against this cart.
            </div>
          </div>
        ) : (
          refunds.map((refundList, index) => {
            return (
              <div
                key={`refund_${index}`}
                className={`tw-w-full tw-flex tw-font-medium tw-text-grey-600 tw-bg-teal-050 tw-border-solid  tw-border-[1px] tw-border-teal-200 tw-rounded-xl ${
                  index === refunds.length - 1 ? 'tw-mb-5' : 'tw-mb-4'
                }`}
              >
                <div className='tw-flex-1 tw-my-3 tw-ml-5 tw-mr-8'>
                  {refundList.fields.map((refund, index) => {
                    if (index % 2 === 0)
                      return (
                        <div className='tw-flex tw-mb-2 tw-justify-between'>
                          <span className='tw-text-200 '>{refund.label}</span>
                          <span className='tw-flex tw-gap-1 tw-items-center tw-justify-end'>
                            {refund?.value.map((valueDetails, index) => {
                              return (
                                <ValueComp
                                  key={`refund_value_even_${index}`}
                                  valueDetails={valueDetails}
                                  metaData={refundList?.meta}
                                  refetchTransactionDetails={
                                    refetchTransactionDetails
                                  }
                                />
                              );
                            })}
                          </span>
                        </div>
                      );
                  })}
                </div>
                <div className='tw-w-1 tw-border-solid tw-border-[1px] tw-border-l-0 tw-border-t-0 tw-border-b-0 tw-border-teal-200 tw-rounded-xl'></div>
                <div className='tw-flex-1 tw-my-3 tw-mr-5 tw-ml-8'>
                  {refundList.fields.map((refund, index) => {
                    if (index % 2 != 0)
                      return (
                        <div className='tw-flex tw-mb-2 tw-justify-between'>
                          <span className='tw-text-200'>{refund.label}</span>
                          <span className='tw-flex tw-gap-1 tw-items-center tw-justify-end'>
                            {refund?.value.map((valueDetails, ind) => {
                              return (
                                <ValueComp
                                  key={`refund_value_odd_${ind}`}
                                  valueDetails={valueDetails}
                                  metaData={refundList?.meta}
                                  refetchTransactionDetails={
                                    refetchTransactionDetails
                                  }
                                />
                              );
                            })}
                          </span>
                        </div>
                      );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

const DetailsCard = ({
  payments,
  cardTitle,
  isRefundData,
  refetchTransactionDetails,
}) => {
  return (
    <div
      className={`tw-h-full tw-flex-col tw-border-solid  tw-border-[1px] tw-border-grey-200 tw-rounded-xl`}
    >
      <div
        className={`tw-w-full tw-flex tw-justify-between tw-p-5 tw-border-solid  tw-border-[1px] tw-border-t-0 tw-border-l-0 tw-border-r-0  tw-border-grey-200 ${
          payments?.length === 0 ? 'tw-rounded-xl' : ''
        }`}
      >
        <div className='tw-flex-col'>
          <span className='tw-flex tw-gap-3 tw-items-center tw-justify-center'>
            <span className='tw-text-400 tw-font-bold tw-cursor-pointer'>
              {cardTitle}
            </span>
            {cardTitle === 'Active Payments' && (
              <span className='tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-mr-1'>
                <img
                  src={
                    'https://b.zmtcdn.com/data/o2_assets/f7427e7fb2c541cae04a7b8c6b0728f21691409653.png'
                  }
                  alt='active_payments'
                  className='tw-w-full tw-h-full'
                />
              </span>
            )}
          </span>
        </div>
      </div>
      {isRefundData ? (
        <RefundDetailsComp
          refunds={payments}
          refetchTransactionDetails={refetchTransactionDetails}
        />
      ) : (
        payments.length > 0 && (
          <div className='tw-flex'>
            <div className=' tw-w-full tw-flex-col'>
              {payments?.map((payment, index) => {
                return (
                  <>
                    <PaymentCards paymentDetails={payment} />
                    {index < payments?.length - 1 && (
                      <div className='tw-my-8'>
                        <hr width='2' size='1' color='#E8E8E8' />
                      </div>
                    )}
                  </>
                );
              })}
            </div>
            <div></div>
          </div>
        )
      )}
    </div>
  );
};

export const TransactionsDetailsV2 = ({
  transactionDetails,
  orderId,
  cartId,
  customerId,
}) => {
  const [transactions, setTransactions] = useState(transactionDetails);
  const [error, setError] = useState();
  const { active_payments, inactive_payments, refunds } = transactions || {};
  const getTransactionDetailsV2EndPoint = `${lifelineEndPoints?.GET_TRANSACTION_DETAILS}?order_id=${orderId}&cart_id=${cartId}&customer_id=${customerId}`;

  const {
    isRefetching: transactionRefetching,
    refetch: refetchTransactionDetails,
  } = useQueryCall(
    {
      enabled: false,
      onSuccess: (res) => {
        setTransactions(res);
      },
      onSettled: (res) => {
        if (res.isError) setError(res.message);
      },
    },
    getTransactionDetailsV2EndPoint,
    [],
  );

  if (error || transactions?.isError)
    return (
      <div
        className='tw-h-full tw-flex tw-items-center tw-justify-center'
        style={{ height: '50vh' }}
      >
        <EmptyState
          img={InvalidSearchImg}
          heading={error || transactions?.message || 'Something went wrong'}
        />
      </div>
    );

  if (transactionRefetching)
    return (
      <div
        className='tw-w-full  tw-flex tw-items-center tw-justify-center'
        style={{ minHeight: '50vh' }}
      >
        <Loader />
      </div>
    );

  if (!transactions)
    return (
      <div
        className='tw-h-full tw-flex tw-items-center tw-justify-center'
        style={{ height: '50vh' }}
      >
        <EmptyState
          img={InvalidSearchImg}
          heading={'No transaction details available on this cart'}
        />
      </div>
    );

  return (
    <>
      <div className='tw-flex tw-flex-col tw-gap-7'>
        {refunds?.length > 0 && (
          <DetailsCard
            payments={refunds}
            cardTitle='Refunds'
            isRefundData={true}
            refetchTransactionDetails={refetchTransactionDetails}
          />
        )}
        {active_payments?.length > 0 ? (
          <DetailsCard payments={active_payments} cardTitle='Active Payments' />
        ) : (
          <div className='tw--mb-7'></div>
        )}
        {inactive_payments?.length > 0 ? (
          <DetailsCard
            payments={inactive_payments}
            cardTitle='Inactive Payments'
          />
        ) : (
          <div className='tw--mb-7'></div>
        )}
      </div>
    </>
  );
};
